<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

/**
 * State-machine wrapper around <StripePayment> (the inner elements form).
 *
 * Owns: post-confirm polling, retry flow (calling the public retry
 * endpoint), and incomplete / failure UX. The inner StripePayment
 * component just submits the payment and emits the result; this
 * wrapper takes that emit and drives the customer through the rest
 * of the lifecycle.
 *
 * Mirrors components/StripePayment/index.vue from old-site (ticket-503).
 */

interface PaymentIntent {
  id?: string;
  client_secret?: string;
  amount?: number;
  status?: string;
  [key: string]: unknown;
}

interface Props {
  orderId: number | string;
  paymentIntent?: PaymentIntent | null;
  bookDelivery?: boolean;
  isPremiumGroupOrder?: boolean;
  /**
   * Pre-seed the view when the parent already knows we're mid-flight
   * (e.g. resume on /checkout mount). Accepts: idle, confirming,
   * incomplete, failure. `null` lets the wrapper decide via Stripe status.
   */
  initialView?: "idle" | "confirming" | "incomplete" | "failure" | null;
  /** Show "Start Again" alongside Retry on the failure view. */
  allowStartAgain?: boolean;
  /** Show a Cancel button on the elements form (group-order checkout). */
  showCancel?: boolean;
  /**
   * When true (default) the wrapper handles retry internally — it calls
   * the retryPayment store action, swaps in the fresh PaymentIntent, and
   * goes back to idle. When false, the wrapper just emits "retry" and
   * the parent owns the retry flow.
   */
  useInternalRetry?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  paymentIntent: null,
  bookDelivery: false,
  isPremiumGroupOrder: false,
  initialView: null,
  allowStartAgain: false,
  showCancel: false,
  useInternalRetry: true,
});

const emit = defineEmits<{
  confirmed: [];
  failed: [];
  incomplete: [];
  retry: [];
  "retry:start": [];
  "retry:success": [paymentIntent: PaymentIntent];
  "retry:failure": [error: unknown];
  "start-again": [];
  cancel: [];
}>();

const VIEWS = {
  IDLE: "idle",
  CONFIRMING: "confirming",
  RETRYING: "retrying",
  INCOMPLETE: "incomplete",
  FAILURE: "failure",
} as const;
type View = (typeof VIEWS)[keyof typeof VIEWS];

const ordersStore = useOrdersStore();
const authStore = useAuthStore();
const { fetchStripePaymentIntentStatus } = useStripe();
const { pollPayLedgerStatus, cancelPayLedgerPolling } = usePayLedgerPolling();

const view = ref<View>((props.initialView as View) || VIEWS.IDLE);

const localPaymentIntent = ref<PaymentIntent | null>(props.paymentIntent);
const cancelled = ref(false);

const isAuthenticated = computed(() => !!authStore.token?.token);

const incompleteBodyCopy = computed(() =>
  isAuthenticated.value
    ? "You'll see this order in Upcoming Orders as soon as it's ready."
    : "We'll email you once your order is confirmed. You can safely close this page. If you have any questions, please contact support.",
);

// Re-init when the parent swaps in a fresh PaymentIntent (retry case).
watch(
  () => props.paymentIntent?.client_secret,
  (newSecret, oldSecret) => {
    if (newSecret && newSecret !== oldSecret) {
      localPaymentIntent.value = props.paymentIntent;
      view.value = VIEWS.IDLE;
    }
  },
);

onMounted(async () => {
  // Parent-driven resume: already decided we're mid-flight. Kick off the
  // poll so the confirming spinner actually resolves.
  if (props.initialView === VIEWS.CONFIRMING) {
    await pollUntilSettled();
    return;
  }
  // Parent supplied a different initial view (incomplete / failure / idle) —
  // honour it without probing Stripe.
  if (props.initialView) return;
  if (!localPaymentIntent.value?.client_secret) return;

  // Cold mount with a PI: ask Stripe what state it's in. If the customer
  // already submitted card details (processing / succeeded), jump straight
  // to confirming + poll until the ledger settles.
  const stripeStatus = await fetchStripePaymentIntentStatus(
    localPaymentIntent.value.client_secret,
  );
  if (stripeStatus) ordersStore.setStripePaymentIntentStatus(stripeStatus);
  if (stripeStatus === "processing" || stripeStatus === "succeeded") {
    view.value = VIEWS.CONFIRMING;
    await pollUntilSettled();
  }
});

onBeforeUnmount(() => {
  cancelled.value = true;
  cancelPayLedgerPolling();
});

async function onPaymentIntentConfirmed() {
  view.value = VIEWS.CONFIRMING;
  ordersStore.setCartLoading(true);
  try {
    await pollUntilSettled();
  } catch {
    ordersStore.setCartLoading(false);
    view.value = VIEWS.IDLE;
  }
}

/**
 * Poll the public pay-ledger-status endpoint until the ledger settles
 * (succeeded / failed) or the budget is exhausted (incomplete).
 * Routes the view + emits per the resolution.
 */
async function pollUntilSettled() {
  const paymentIntentId = localPaymentIntent.value?.id;
  if (!paymentIntentId) {
    ordersStore.setCartLoading(false);
    view.value = VIEWS.INCOMPLETE;
    emit("incomplete");
    return;
  }

  const result = await pollPayLedgerStatus({ paymentIntentId });

  if (cancelled.value) return;

  ordersStore.setCartLoading(false);

  if (result === "succeeded") {
    emit("confirmed");
    return;
  }
  if (result === "failed") {
    view.value = VIEWS.FAILURE;
    emit("failed");
    return;
  }
  // timeout / cancelled both land here as incomplete — webhook is in
  // flight or the user navigated. Show the passive "we'll email you"
  // view rather than prompting for new card details.
  view.value = VIEWS.INCOMPLETE;
  emit("incomplete");
}

async function retry() {
  ordersStore.setCheckoutError(false);

  if (!props.useInternalRetry) {
    view.value = VIEWS.IDLE;
    emit("retry");
    return;
  }

  const paymentIntentId = localPaymentIntent.value?.id;
  if (!paymentIntentId) {
    emit("retry");
    return;
  }

  view.value = VIEWS.RETRYING;
  emit("retry:start");
  try {
    const res = await ordersStore.retryPayment({ paymentIntentId });
    localPaymentIntent.value = res.payment_intent;
    ordersStore.setStripePaymentIntentStatus(null);
    view.value = VIEWS.IDLE;
    emit("retry:success", res.payment_intent);
  } catch (error) {
    view.value = VIEWS.FAILURE;
    emit("retry:failure", error);
  }
}

function startAgain() {
  ordersStore.setCheckoutError(false);
  emit("start-again");
}

function goToOrders() {
  navigateTo("/orders/upcoming");
}

function onCancel() {
  emit("cancel");
}
</script>

<template>
  <div class="stripe-payment-wrapper">
    <div v-if="view === 'confirming'" class="stripe-payment-wrapper__status">
      <fa :icon="['fal', 'spinner']" class="fa-spin stripe-payment-wrapper__spinner" />
      <p><strong>Processing your payment</strong></p>
      <p>This can take a few seconds. Please don't close this window.</p>
    </div>

    <div v-else-if="view === 'retrying'" class="stripe-payment-wrapper__status">
      <fa :icon="['fal', 'spinner']" class="fa-spin stripe-payment-wrapper__spinner" />
      <p><strong>Setting up your retry</strong></p>
      <p>This can take a few seconds. Please don't close this window.</p>
    </div>

    <div v-else-if="view === 'incomplete'" class="stripe-payment-wrapper__status">
      <fa :icon="['fal', 'circle-info']" class="stripe-payment-wrapper__icon-large" />
      <p><strong>We're finalising your payment</strong></p>
      <p>{{ incompleteBodyCopy }}</p>
      <DynamicButton
        v-if="isAuthenticated"
        class="stripe-payment-wrapper__action"
        color="pink"
        text="Orders"
        @click-event="goToOrders"
      />
    </div>

    <div v-else-if="view === 'failure'" class="stripe-payment-wrapper__status">
      <fa
        :icon="['fal', 'circle-exclamation']"
        class="stripe-payment-wrapper__icon-error"
      />
      <p><strong>Payment failed</strong></p>
      <p>
        {{
          allowStartAgain
            ? "Please retry payment or clear your basket and start again"
            : "Please retry your payment."
        }}
      </p>
      <DynamicButton
        class="stripe-payment-wrapper__action"
        color="pink"
        text="Retry Payment"
        @click-event="retry"
      />
      <DynamicButton
        v-if="allowStartAgain"
        class="stripe-payment-wrapper__action"
        color="transparent-black"
        text="Start Again"
        @click-event="startAgain"
      />
    </div>

    <StripePayment
      v-else-if="localPaymentIntent && localPaymentIntent.client_secret"
      :book-delivery="bookDelivery"
      :payment-intent="localPaymentIntent as never"
      :is-premium-group-order="isPremiumGroupOrder"
      :show-cancel="showCancel"
      @payment:confirmed="onPaymentIntentConfirmed"
      @payment:cancel="onCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
.stripe-payment-wrapper {
  width: 100%;

  &__status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 1rem;
    text-align: center;
  }

  &__spinner {
    height: 2rem;
    width: 2rem;
  }

  &__icon-large {
    height: 6.25rem;
    width: 6.25rem;
  }

  &__icon-error {
    color: #ff1e5f;
    height: 2rem;
    width: 2rem;
  }

  &__action {
    margin-top: 0.5rem;
  }
}
</style>
